"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.POST_EXCERPT_MAX_PARAGRAPHS = exports.POST_EXCERPT_MAX_CHARS = exports.PROMPT_FROM_POST_MAX_TEXT = exports.POST_PROMPT_STYLE_LABEL = exports.POST_PROMPT_STYLES = void 0;
exports.postExcerpt = postExcerpt;
/** The pills above the prompt field. Each kind offers its own; the server refuses a mismatch. */
exports.POST_PROMPT_STYLES = {
    image: ['infographic', 'illustration', 'photo'],
    abstract: ['gradient-mesh', 'grainy-texture', 'geometric-shapes'],
};
/** What each pill says. */
exports.POST_PROMPT_STYLE_LABEL = {
    infographic: 'Infographic',
    illustration: 'Illustration',
    photo: 'Photo',
    'gradient-mesh': 'Gradient mesh',
    'grainy-texture': 'Grainy texture',
    'geometric-shapes': 'Geometric shapes',
};
/**
 * The most a request may carry. Far above any post: the window sends only `postExcerpt`, and the
 * server trims again, so this only stops a runaway body. 100,000 characters is at most 600KB of
 * JSON even if every one were escaped as \uXXXX, under the API's 1MB body limit, so a request this
 * long is refused with this message rather than a bare 413.
 */
exports.PROMPT_FROM_POST_MAX_TEXT = 100_000;
/** A post this long or shorter is read whole; a longer one is read from its opening. */
exports.POST_EXCERPT_MAX_CHARS = 6_000;
/** The most paragraphs a long post's opening is read from. */
exports.POST_EXCERPT_MAX_PARAGRAPHS = 5;
/** Shorter than this, a block is a heading, a byline or a bullet: it is kept, but not counted. */
const PARAGRAPH_MIN_CHARS = 80;
/** A block this long with single line breaks in it is paragraphs pasted without blank lines. */
const RUN_ON_BLOCK_CHARS = 2_000;
/** Room for less than this is not worth a piece of the next paragraph. */
const MIN_PIECE_CHARS = 200;
/** A block that says nothing to draft from: an image, a bare link, a line of hashtags. */
const NOISE = /^(?:!\[[^\]]*\]\([^)]*\)|https?:\/\/\S+|(?:#[\p{L}\p{N}_]+\s*)+)$/u;
/**
 * The part of a post a prompt is drafted from. A post up to `POST_EXCERPT_MAX_CHARS` is read whole.
 * A longer one (a full blog post) is read from its opening: the opening says what a post is about,
 * and a prompt is one paragraph. Its first paragraphs, up to five, are taken while they fit; the
 * first that does not fit whole is taken up to its last sentence end that does, so a short hook
 * line is never all the model sees. Images, bare links and hashtag lines are dropped.
 */
function postExcerpt(text) {
    const clean = text.replace(/\r\n?/g, '\n').trim();
    if (clean.length <= exports.POST_EXCERPT_MAX_CHARS)
        return { text: clean, partial: false };
    const blocks = postBlocks(clean);
    let excerpt = '';
    let paragraphs = 0;
    let used = 0;
    for (const block of blocks) {
        if (paragraphs >= exports.POST_EXCERPT_MAX_PARAGRAPHS)
            break;
        const joined = excerpt ? `${excerpt}\n\n${block}` : block;
        if (joined.length > exports.POST_EXCERPT_MAX_CHARS) {
            const room = exports.POST_EXCERPT_MAX_CHARS - (excerpt ? excerpt.length + 2 : 0);
            if (room >= MIN_PIECE_CHARS) {
                const piece = cutToFit(block, room);
                excerpt = excerpt ? `${excerpt}\n\n${piece}` : piece;
            }
            break;
        }
        excerpt = joined;
        used += 1;
        if (block.length >= PARAGRAPH_MIN_CHARS)
            paragraphs += 1;
    }
    // Nothing but images, links and hashtags: the post's own opening, so there is still something.
    if (!excerpt)
        return { text: cutToFit(clean, exports.POST_EXCERPT_MAX_CHARS), partial: true };
    return { text: excerpt, partial: used < blocks.length };
}
/** A post's paragraphs, with the noise left out. */
function postBlocks(clean) {
    return (clean
        .split(/\n\s*\n/)
        // A title, a blank line, then a body with single line breaks between its paragraphs: the body
        // is one block until it is split on those breaks.
        .flatMap((block) => block.length > RUN_ON_BLOCK_CHARS && block.includes('\n') ? block.split('\n') : [block])
        .map((block) => block.trim())
        .filter((block) => block.length > 0 && !NOISE.test(block)));
}
/** `text` cut to at most `max` characters: at a sentence end, else a space, never mid-emoji. */
function cutToFit(text, max) {
    if (text.length <= max)
        return text;
    let cut = text.slice(0, max);
    if (/[\uD800-\uDBFF]$/.test(cut))
        cut = cut.slice(0, -1);
    const sentenceEnd = Math.max(...['. ', '! ', '? ', '。', '！', '？'].map((m) => cut.lastIndexOf(m)));
    if (sentenceEnd > max / 2)
        return cut.slice(0, sentenceEnd + 1).trimEnd();
    const space = cut.search(/\s\S*$/);
    return (space > max / 2 ? cut.slice(0, space) : cut).trimEnd();
}
//# sourceMappingURL=prompt-from-post.js.map